import Joi from '@hapi/joi';
import { chain } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { ObjectTypeFieldDataType } from '../../../../common/src/domain/settings/objectType/ObjectTypeFieldDataType';
import { PickItemsType, OpenApiRouteResultType } from '../../../../common/src/routes/openApiTypes';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchJson } from '../../store/fetch';
import { openApiFetch } from '../../store/openApiFetch';
import { mainMaterialList } from '../../utils/data/mainMaterialList';
import { trafficTypeList } from '../../utils/data/trafficTypeList';
import { joinEvenAndUnevenRows } from '../../utils/joinEvenAndUnevenRows';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    EditPanelSection,
    PanelBody,
    PanelHeader,
    Tray,
} from '../elements';
import { showSavedToast } from '../utils/toastMessages';
import {
    ValidatingCheckboxList,
    ValidatingCulturedFormInputNumber,
    ValidatingEditPanelSaveButton,
    ValidatingEditPanelSwitch,
    ValidatingForm,
    ValidatingFormDependentSection,
    ValidatingFormInputSelect,
    ValidatingFormInputText,
} from '../validatingElements';

type Field = PickItemsType<OpenApiRouteResultType<'/api/objectTypes/{objectTypeId}/objectTypeFields', 'get'>>;

function getEditField(field: Field, selectText: string) {
    const topLabel = field.unit ? `${field.name} (${field.unit})` : `${field.name}`;

    const componentName = `objectTypeFieldsValues.${field.id}`;

    switch (field.dataType) {
        case ObjectTypeFieldDataType.String:
            return <ValidatingFormInputText name={componentName} topLabel={topLabel} />;
        case ObjectTypeFieldDataType.Number:
            return <ValidatingCulturedFormInputNumber name={componentName} topLabel={topLabel} />;
        case ObjectTypeFieldDataType.Boolean:
            return <ValidatingEditPanelSwitch name={componentName} placeholder={topLabel} />;
        case ObjectTypeFieldDataType.List:
            if (field.listOptions.length === 0) {
                return <div>No options for {topLabel}</div>;
            }

            return (
                <ValidatingFormInputSelect
                    name={componentName}
                    topLabel={topLabel}
                    options={field.listOptions.map((option) => ({ text: option.value, value: option.value }))}
                    placeholder={selectText}
                />
            );
        default:
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            throw new Error(`Unknown dataType "${field.dataType}"`);
    }
}

const schema = Joi.object().keys({
    constructionYear: Joi.number().integer().less(2100).allow(null),
    length: Joi.number().less(999999999).allow(null),
    width: Joi.number().less(999999999).allow(null),
    squareMeters: Joi.number().less(999999999).allow(null),
    trafficType: Joi.string().allow(null, ''),
    mainMaterial: Joi.string().allow(null, ''),
    marineInfrastrutureType: Joi.string().allow(null, ''),
    managementOrganization: Joi.string().allow(null, ''),
    attributes: Joi.object(),
    combinedConstructionYear: Joi.string().allow(null, ''),
    province: Joi.string().allow(null, ''),
    municipality: Joi.string().allow(null, ''),
    complexName: Joi.string().allow(null, ''),
    complexCode: Joi.string().allow(null, ''),
    complexLocation: Joi.string().allow(null, ''),
    externalRefId: Joi.string().allow(null, ''),
    objectTypeFieldsValues: Joi.object(),
});

// eslint-disable-next-line @typescript-eslint/naming-convention
export function ObjectMetaData(props: { objectId: string }) {
    const { objectId } = props;
    const translate = useTranslate();

    const [objectTypeFields, setObjectTypeFields] = useState<OpenApiRouteResultType<'/api/objectTypes/{objectTypeId}/objectTypeFields', 'get'>>({
        items: [],
    });

    useEffect(() => {
        async function load() {
            const objectResult = await fetchJson(`/api/objects/${objectId}`, 'GET');
            const { clientCompanyId, objectTypeId } = objectResult.json;
            if (clientCompanyId) {
                const fields = await openApiFetch(
                    `/api/objectTypes/{objectTypeId}/objectTypeFields`,
                    'get',
                    { objectTypeId, clientCompanyId },
                    undefined,
                );
                setObjectTypeFields(fields);
            }
        }
        void load();
    }, [objectId]);

    const groupedItems = chain(objectTypeFields.items)
        .groupBy('category')
        .map((value, key) => ({ category: key, leftAndRightItems: joinEvenAndUnevenRows(value) }))
        .value();

    return (
        <Tray>
            <ValidatingForm
                schema={schema}
                itemUrl={`/api/objects/${props.objectId}`}
                updateUrl={`/api/objects/general/${props.objectId}`}
                id={props.objectId}
                onSubmitted={() => showSavedToast(translate)}
            >
                <EditPanel bottomMargin={true} noPadding={true}>
                    <CollapsiblePanel heading={{ dark: true, text: translate('common.dimensions'), initialIsClosed: true }}>
                        <PanelBody isForm={true}>
                            <EditPanelRow>
                                <EditPanelCell rowCount={3}>
                                    <ValidatingCulturedFormInputNumber name="length" topLabel={`${translate('object.length')} (m)`} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={3}>
                                    <ValidatingCulturedFormInputNumber name="width" topLabel={`${translate('object.width')} (m)`} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={3}>
                                    <ValidatingCulturedFormInputNumber name="attributes.height" topLabel={`${translate('object.height')} (m)`} />
                                </EditPanelCell>
                            </EditPanelRow>

                            <EditPanelRow>
                                <EditPanelCell rowCount={3}>
                                    <ValidatingCulturedFormInputNumber name="squareMeters" topLabel={`${translate('object.squareMeters')} (㎡)`} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={3}>
                                    <ValidatingCulturedFormInputNumber name="attributes.volume" topLabel={`${translate('object.volume')} (㎥)`} />
                                </EditPanelCell>
                            </EditPanelRow>

                            <EditPanelButtons>
                                <ValidatingEditPanelSaveButton small={true} />
                            </EditPanelButtons>
                        </PanelBody>
                    </CollapsiblePanel>
                </EditPanel>

                <EditPanel bottomMargin={true} noPadding={true}>
                    <CollapsiblePanel heading={{ dark: true, text: 'Complex', initialIsClosed: true }}>
                        <PanelBody isForm={true}>
                            <EditPanelRow>
                                <EditPanelCell rowCount={9}>
                                    <ValidatingFormInputText name="complexName" topLabel={translate('object.complexName')} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={3}>
                                    <ValidatingFormInputText name="complexCode" topLabel={translate('object.complexCode')} />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    <ValidatingFormInputText name="complexLocation" topLabel={translate('object.locationDescription')} />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    <ValidatingFormInputText name="externalRefId" topLabel={translate('object.externalReference')} />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelButtons>
                                <ValidatingEditPanelSaveButton small={true} />
                            </EditPanelButtons>
                        </PanelBody>
                    </CollapsiblePanel>
                </EditPanel>

                <EditPanel bottomMargin={true} noPadding={true}>
                    <CollapsiblePanel heading={{ dark: true, text: translate('common.classification'), initialIsClosed: true }}>
                        <PanelBody isForm={true}>
                            <EditPanelRow>
                                <EditPanelCell rowCount={6}>
                                    <ValidatingFormInputText name="managementOrganization" topLabel={translate('object.managementOrganization')} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={6}>
                                    <ValidatingFormInputText name="attributes.area" topLabel={translate('object.area')} />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={6}>
                                    <ValidatingFormInputText name="province" topLabel={translate('common.province')} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={6}>
                                    <ValidatingFormInputText name="municipality" topLabel={translate('common.municipality')} />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelButtons>
                                <ValidatingEditPanelSaveButton small={true} />
                            </EditPanelButtons>
                        </PanelBody>
                    </CollapsiblePanel>
                </EditPanel>

                <EditPanel bottomMargin={true} noPadding={true}>
                    <CollapsiblePanel heading={{ dark: true, text: translate('object.construction'), initialIsClosed: true }}>
                        <PanelBody isForm={true}>
                            <EditPanelRow>
                                <EditPanelCell rowCount={3}>
                                    <ValidatingCulturedFormInputNumber
                                        name="attributes.replacementValue"
                                        topLabel={`${translate('object.replacementValue')} (€)`}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>

                            <EditPanelRow>
                                <EditPanelCell rowCount={3}>
                                    <ValidatingCulturedFormInputNumber name="constructionYear" topLabel={translate('object.constructionYear')} />
                                </EditPanelCell>
                                <EditPanelCell rowCount={5}>
                                    <ValidatingFormInputText name="attributes.construction" topLabel={translate('object.construction')} />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={12}>
                                    <ValidatingFormInputText
                                        name="attributes.combinedConstructionYear"
                                        topLabel={translate('object.combinedConstructionYear')}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <ValidatingFormDependentSection showIf={(item) => item.inspectionStandardType === 'nen2767'}>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={4}>
                                        <ValidatingFormInputSelect
                                            name="mainMaterial"
                                            topLabel={translate('object.mainMaterial')}
                                            options={mainMaterialList}
                                            placeholder={`${translate('common.select')}...`}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={4}>
                                        <ValidatingFormInputSelect
                                            name="attributes.palen"
                                            topLabel="Palen"
                                            options={[
                                                { text: 'Hout', value: 'Hout' },
                                                { text: 'Beton', value: 'Beton' },
                                            ]}
                                            placeholder={`${translate('common.select')}...`}
                                        />
                                    </EditPanelCell>

                                    <EditPanelCell rowCount={4}>
                                        <ValidatingFormInputSelect
                                            name="attributes.damwandplanken"
                                            topLabel="Damwandplanken"
                                            options={[
                                                { text: 'N.v.t.', value: 'N.v.t.' },
                                                { text: 'Hout', value: 'Hout' },
                                                { text: 'Beton', value: 'Beton' },
                                                { text: 'Kunststof', value: 'Kunststof' },
                                            ]}
                                            placeholder={`${translate('common.select')}...`}
                                        />
                                    </EditPanelCell>

                                    <EditPanelCell rowCount={4}>
                                        <ValidatingFormInputSelect
                                            name="attributes.dragers"
                                            topLabel="Dragers"
                                            options={[
                                                { text: 'Gewapend beton', value: 'Gewapend beton' },
                                                { text: 'Gewapend beton - aluminium', value: 'Gewapend beton - aluminium' },
                                                { text: 'Gewapend beton - hout', value: 'Gewapend beton - hout' },
                                                { text: 'Gewapend beton - hout - composiet', value: 'Gewapend beton - hout - composiet' },
                                                { text: 'Gewapend beton - staal', value: 'Gewapend beton - staal' },
                                                { text: 'Gewapend beton - staal - composiet', value: 'Gewapend beton - staal - composiet' },
                                                { text: 'Gewapend beton - staal - hout', value: 'Gewapend beton - staal - hout' },
                                                { text: 'Hout', value: 'Hout' },
                                                { text: 'Hout - composiet', value: 'Hout - composiet' },
                                                { text: 'Hout - staal', value: 'Hout - staal' },
                                                { text: 'Metselwerk - gewapend beton', value: 'Metselwerk - gewapend beton' },
                                                { text: 'Staal', value: 'Staal' },
                                                { text: 'Staal - beton', value: 'Staal - beton' },
                                                { text: 'Staal - gewapend beton', value: 'Staal - gewapend beton' },
                                                { text: 'Staal - hout', value: 'Staal - hout' },
                                                { text: 'Staal - metselwerk - gewapend beton', value: 'Staal - metselwerk - gewapend beton' },
                                                { text: 'Stalen liggers - gewapend beton', value: 'Stalen liggers - gewapend beton' },
                                                { text: 'Vgs prefabbeton - gewapend beton', value: 'Vgs prefabbeton - gewapend beton' },
                                                { text: 'Welfbrug metselwerk', value: 'Welfbrug metselwerk' },
                                                { text: 'Welfbrug metselwerk - gewapend beton', value: 'Welfbrug metselwerk - gewapend beton' },
                                            ]}
                                            placeholder={`${translate('common.select')}...`}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>

                                <PanelHeader text="Belasting" />
                                <EditPanelRow>
                                    <EditPanelCell rowCount={4}>
                                        <ValidatingFormInputSelect
                                            name="attributes.loadClass"
                                            topLabel={translate('object.loadClass')}
                                            options={[
                                                { text: 'BK-30-1963', value: 'BK-30-1963' },
                                                { text: 'BK-A-1938', value: 'BK-A-1938' },
                                                { text: 'BK-B-1938', value: 'BK-B-1938' },
                                                { text: 'BK-C-1938', value: 'BK-C-1938' },
                                                { text: 'BK-D-1938', value: 'BK-D-1938' },
                                                { text: 'BK-30-1963', value: 'BK-30-1963' },
                                                { text: 'BK-45-1963', value: 'BK-45-1963' },
                                                { text: 'BK-60-1963', value: 'BK-60-1963' },
                                                { text: '>60', value: '> 60' },
                                                { text: '<60', value: '< 30' },
                                                { text: 'non', value: translate('common.non') },
                                            ]}
                                            placeholder={`${translate('common.select')}...`}
                                        />
                                    </EditPanelCell>
                                    <EditPanelCell rowCount={8}>
                                        <ValidatingFormInputText name="attributes.loadClassExplanation" topLabel={translate('common.explanation')} />
                                    </EditPanelCell>
                                </EditPanelRow>
                            </ValidatingFormDependentSection>
                            <PanelHeader text={translate('object.trafficType')} />
                            <EditPanelRow>
                                <ValidatingCheckboxList name="trafficType" options={trafficTypeList} />
                            </EditPanelRow>
                            <EditPanelButtons>
                                <ValidatingEditPanelSaveButton small={true} />
                            </EditPanelButtons>
                        </PanelBody>
                    </CollapsiblePanel>
                </EditPanel>

                {groupedItems.length > 0 && (
                    <EditPanel bottomMargin={true} noPadding={true}>
                        <CollapsiblePanel heading={{ dark: true, text: translate('objectTypeField.additionalData'), initialIsClosed: false }}>
                            <PanelBody isForm={true}>
                                {groupedItems.map((category) => (
                                    <Fragment key={category.category}>
                                        {category.category && (
                                            <EditPanelSection>
                                                <PanelHeader text={category.category} />
                                            </EditPanelSection>
                                        )}
                                        {category.leftAndRightItems.map((leftAndRightItem) => (
                                            <EditPanelRow key={leftAndRightItem.left.id}>
                                                <EditPanelCell rowCount={6}>
                                                    {getEditField(leftAndRightItem.left, `${translate('common.select')}...`)}
                                                </EditPanelCell>
                                                {leftAndRightItem.right && (
                                                    <EditPanelCell rowCount={6}>
                                                        {getEditField(leftAndRightItem.right, `${translate('common.select')}...`)}
                                                    </EditPanelCell>
                                                )}
                                            </EditPanelRow>
                                        ))}
                                    </Fragment>
                                ))}
                                <EditPanelButtons>
                                    <ValidatingEditPanelSaveButton small={true} />
                                </EditPanelButtons>
                            </PanelBody>
                        </CollapsiblePanel>
                    </EditPanel>
                )}
            </ValidatingForm>
        </Tray>
    );
}
